/**
 * Realtime subscription wrapper — NEUTRALIZED for the PB→Django cutover.
 *
 * Historically this wrapped PocketBase's SSE realtime
 * (`pb.collection(X).subscribe(...)`, served at `<pb>/api/realtime`).
 * PocketBase has been decommissioned (v2.1 — strangler-fig migration to
 * Django + Postgres). Django does not expose PB's SSE endpoint, so this
 * wrapper is now a no-op: callers still get a stable `(collection, cb) =>
 * unsubscribe` contract, but no live events are delivered. Consumers
 * (`useWorkspace`, `LibraryPage`) already refetch on mount / navigation /
 * explicit events, so this degrades to "updates on next navigation"
 * rather than breaking.
 *
 * Forward path: Django Channels (`/ws/*`, Daphne) is the realtime
 * transport now — generation job progress already uses it. Re-adding
 * live collection push (workspaces / assets / credits) over Channels is
 * a Phase 2 follow-up; intentionally out of scope for the cutover.
 *
 * Keeping this module (instead of deleting it) preserves the import
 * surface so no caller needs to change during the cutover.
 */

export interface PbRealtimeEvent<T = Record<string, unknown>> {
  action: "create" | "update" | "delete";
  record: T;
}

type Callback<T = Record<string, unknown>> = (event: PbRealtimeEvent<T>) => void;

/**
 * No-op subscribe. Accepts the same args as before and returns an
 * unsubscribe function so existing call sites keep working unchanged.
 * The callback is never invoked (PB SSE removed; see module header).
 */
function subscribe<T = Record<string, unknown>>(
  _collection: string,
  _callback: Callback<T>,
): () => void {
  return () => { /* nothing to tear down — PB realtime removed */ };
}

export const realtime = { subscribe };
export default realtime;
